import { createContext, useContext, useEffect, useId } from "react";
import { setUnsaved } from "../../app/update.js";

/**
 * Несохранённые правки шага мастера настройки. Шаг (или его карточка) сообщает наверх `useUnsavedChanges(dirty)`,
 * страница мастера перед уходом спрашивает подтверждение: переходы по шагам внутри мастера (адрес не меняется)
 * через отложенный переход (`requestLeave`), уход на другой маршрут (боковая панель, нижние вкладки, «В меню»,
 * «назад» и «вперёд» браузера) через блокировщик роутера (`shouldBlockNavigation`), закрытие и перезагрузка
 * вкладки через `beforeunload` (`useBeforeUnloadGuard`). «Далее» с сохранением это не касается.
 */
export interface UnsavedContextValue {
  /** Сообщить состояние источника: `id` различает несколько карточек одного шага. */
  report: (id: string, dirty: boolean) => void;
}

export const UnsavedContext = createContext<UnsavedContextValue | null>(null);

/** Сообщает странице мастера, есть ли на шаге несохранённые правки; при размонтировании шага снимает отметку. */
export function useUnsavedChanges(dirty: boolean): void {
  const ctx = useContext(UnsavedContext);
  const id = useId();
  useEffect(() => {
    ctx?.report(id, dirty);
  }, [ctx, id, dirty]);
  useEffect(() => () => ctx?.report(id, false), [ctx, id]);
}

/**
 * Пока есть правки, закрытие и перезагрузка вкладки показывают стандартный диалог браузера (текст задаёт браузер).
 * Слушатель висит только при правках и снимается при размонтировании. iOS Safari `beforeunload` не шлёт: там защиты нет.
 */
export function useBeforeUnloadGuard(dirty: boolean): void {
  const id = useId();
  // Те же правки держат и автоперезагрузку после выкладки (app/update.ts): вместо неё покажется плашка.
  useEffect(() => {
    setUnsaved(id, dirty);
    return () => setUnsaved(id, false);
  }, [id, dirty]);
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
}

/**
 * Блокировать ли переход роутера: только при правках и только на другой путь. Смена параметров адреса на том же пути
 * (например, кабинет снимает `?n=` после открытия из push) мастер не покидает и вопроса не заслуживает.
 */
export function shouldBlockNavigation(dirty: boolean, currentPath: string, nextPath: string): boolean {
  return dirty && currentPath !== nextPath;
}

/** Есть ли правки: сравнение снимков формы по содержимому (поля в одном порядке, как в форме). */
export function isDirty<T>(initial: T | null | undefined, current: T | null | undefined): boolean {
  if (initial == null || current == null) return false;
  return JSON.stringify(initial) !== JSON.stringify(current);
}

/** Состояние защиты от потери правок: какие источники грязные и отложенный переход, ждущий ответа. */
export interface GuardState {
  dirty: ReadonlySet<string>;
  pending: (() => void) | null;
}

export const initialGuard: GuardState = { dirty: new Set(), pending: null };

/** Источник сообщил своё состояние. */
export function reportDirty(state: GuardState, id: string, dirty: boolean): GuardState {
  if (state.dirty.has(id) === dirty) return state;
  const next = new Set(state.dirty);
  if (dirty) next.add(id);
  else next.delete(id);
  return { ...state, dirty: next };
}

/**
 * Просьба перейти на другой шаг (адрес не меняется, блокировщик роутера не участвует): без правок переход выполняется
 * сразу, с правками откладывается до ответа в шторке. Возвращает новое состояние и признак, что переход выполнен.
 */
export function requestLeave(state: GuardState, go: () => void): { state: GuardState; left: boolean } {
  if (state.dirty.size === 0) {
    go();
    return { state, left: true };
  }
  return { state: { ...state, pending: go }, left: false };
}

/** Ответ «Уйти»: выполняет отложенный переход; отметки снимет сам шаг при размонтировании. */
export function confirmLeave(state: GuardState): GuardState {
  state.pending?.();
  return state.pending ? { ...state, pending: null } : state;
}

/** Ответ «Остаться»: переход отменяется, правки на месте. */
export function stay(state: GuardState): GuardState {
  return state.pending ? { ...state, pending: null } : state;
}
